import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Category, CategoryType } from "@/types/finance";
import { FinanceService } from "../services/financeService";
import { useFinanceContext } from "../contexts/FinanceContext";
import CategoryModal from "./CategoryModal";
import CategoryItem from "./CategoryItem";
import { ArrowLeft, Plus } from "lucide-react";
import { categoriesApiV2, sheetsApiV2 } from "@/services/apiV2";

const CategoryScreen: React.FC = () => {
  const {
    categories,
    sheets,
    setSheets,
    setCategories,
    addCategory,
    updateCategory,
    deleteCategory,
  } = useFinanceContext();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [type, setType] = useState<CategoryType>("income");
  const [parentId, setParentId] = useState<string>("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentSheetId, setCurrentSheetId] = useState<string>("");
  const [sheetTitle, setSheetTitle] = useState("");

  // Escolhe uma planilha "ativa" (principal ou primeira disponível)
  useEffect(() => {
    const preferred =
      sheets.find((s) => s.principal)?.id ?? sheets[0]?.id ?? "";
    if (preferred && preferred !== currentSheetId) setCurrentSheetId(preferred);
    if (!preferred) setCurrentSheetId("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheets]);

  const currentSheet = useMemo(
    () => sheets.find((s) => s.id === currentSheetId),
    [sheets, currentSheetId]
  );

  // Mantém o input de título sincronizado com a planilha atual
  useEffect(() => {
    setSheetTitle(currentSheet?.title ?? "");
  }, [currentSheetId, currentSheet?.title]);

  const availableParents = categories.filter((cat) => {
    if (!editingId) return true;
    if (cat.id === editingId) return false;
    return !FinanceService.isDescendant(editingId, cat.id, categories);
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name) {
      // Renomeia planilha (localmente) se foi alterado no modal
      // const nextTitle = sheetTitle.trim();
      // if (currentSheetId && nextTitle && currentSheet?.title !== nextTitle) {
      //   setSheets((prev) =>
      //     prev.map((s) =>
      //       s.id === currentSheetId ? { ...s, title: nextTitle } : s
      //     )
      //   );
      // }

      if (editingId) {
        updateCategory(editingId, name, type, parentId || null);
      } else {
        addCategory(name, type, parentId || null, currentSheetId);
      }
      closeModal();
    }
  };

  const openAddModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const addSheet = () => {
    alert("Em breve você poderá adicionar planilhas!");
    return;
    // sheetsApiV2.create({
    //   title: sheetTitle,
    //   user_id: authApiV2.getUser()?.id ?? "",
    //   principal: false,
    //   created_at: new Date(),
    // });
  };

  const openEditModal = (category: Category) => {
    setName(category.name);
    setType(category.type);
    setParentId(category.parentId || "");
    setEditingId(category.id);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    resetForm();
    setIsModalOpen(false);
  };

  const resetForm = () => {
    setName("");
    setType("income");
    setParentId("");
    setEditingId(null);
  };

  const handleDelete = (id: string, name: string) => {
    const hasChildren = categories.some((c) => c.parentId === id);
    const message = hasChildren
      ? `A categoria "${name}" possui subcategorias. Excluir esta categoria também excluirá TODA a árvore abaixo dela. Deseja continuar?`
      : `Deseja realmente excluir a categoria "${name}"?`;

    if (window.confirm(message)) {
      categoriesApiV2.delete(id).then(() => {
        deleteCategory(id);
      });
      // deleteCategory(id);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8 relative min-h-screen">
      <div className="flex items-center justify-between mb-8 gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/main")}
            className="p-2 hover:bg-zinc-200 rounded-full transition-colors text-zinc-600"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-2xl font-black text-zinc-800 tracking-tight">
            Gerenciar Planilha
          </h1>
        </div>

        <button
          onClick={addSheet}
          className="flex items-center gap-2 px-6 py-3 bg-[#3f83f8] hover:bg-blue-600 text-white rounded-2xl font-bold shadow-lg shadow-blue-100 transition-all active:scale-95"
        >
          <Plus className="w-5 h-5" />
          <span className="hidden sm:inline">Nova Planilha</span>
        </button>
      </div>

      <div className="bg-white p-5 rounded-2xl border border-zinc-200 shadow-sm flex flex-col relative overflow-hidden">
        <form onSubmit={() => {}} className="space-y-6">
          <div>
            <label className="block text-xl font-black text-zinc-400 uppercase tracking-widest mb-2 ml-1">
              Nome da planilha
            </label>
            <div className="flex items-center justify-between">
              <input
                type="text"
                className="w-full px-5 py-4 border border-zinc-100 bg-zinc-50 rounded-2xl focus:bg-white focus:ring-2 focus:ring-[#3f83f8] outline-none font-bold text-zinc-800"
                placeholder="Ex: Minha Planilha"
                value={sheetTitle}
                onChange={(e) => {
                  setSheetTitle(e.target.value);
                  if (currentSheetId) {
                    sheetsApiV2.updateTitle(currentSheetId, e.target.value);
                  }
                }}
              />
            </div>
          </div>
        </form>
      </div>

      <div className="bg-white p-5 rounded-2xl border border-zinc-200 shadow-sm flex flex-col relative mt-5">
        <div className="flex items-center justify-between">
          <label className="block text-xl font-black text-zinc-400 uppercase tracking-widest">
            Categorias
          </label>
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 px-3 py-3 bg-[#3f83f8] hover:bg-blue-600 text-white rounded-2xl font-bold shadow-lg shadow-blue-100 transition-all active:scale-95"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-1  pr-2 ">
          {categories
            .filter((c) => !c.parentId)
            .map((root) => (
              <CategoryItem
                key={root.id}
                category={root}
                allCategories={categories}
                onEdit={openEditModal}
                onDelete={handleDelete}
                level={0}
              />
            ))}
          {categories.length === 0 && (
            <div className="py-20 text-center text-zinc-400 font-medium italic">
              Nenhuma categoria encontrada.
            </div>
          )}
        </div>
      </div>

      <CategoryModal
        isOpen={isModalOpen}
        sheetTitle={sheetTitle}
        setSheetTitle={setSheetTitle}
        editingId={editingId}
        name={name}
        setName={setName}
        type={type}
        setType={setType}
        parentId={parentId}
        setParentId={setParentId}
        availableParents={availableParents}
        onClose={closeModal}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default CategoryScreen;
